import React, { useEffect, useState } from "react";
import {
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  useTheme,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import TooltipDialogCst from "@components/TooltipDialogCst";
import { useFetchOptions } from "@hooks/useFetchOptions";
import { AutocompleteInterface } from "../AutocompleteCst/types";

const AutocompleteEventLabelCst: React.FC<AutocompleteInterface> = (props) => {
  const { t } = useTranslation(["input"]);
  const [open, setOpen] = useState(false);

  const {
    id,
    label,
    value,
    placeholder = t("generic-placeholder") + label,
    onChange,
    onChangePagination,
    getOptionDisabled,
    options = [],
    customWidth,
    error = false,
    errorMessage = "",
    disabled = false,
    textTooltip,
    required,
    optionsKey,
    isFilterActive,
    sx,
  } = props;

  const theme = useTheme();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(false);
  }, [options]);

  const { getOptions } = useFetchOptions("", optionsKey, options);

  function CustomPaper(props: any) {
    let { children, ...others } = props;
    return (
      <Paper
        variant="outlined"
        role="list-box"
        {...others}
        sx={{ overflow: "hidden!important", color: "white" }}
      >
        {children}
      </Paper>
    );
  }

  type Option = { value: string | number; label: string | number };

  const getSelectedOption = (): Option | null => {
    if (value === null || value === undefined || value === "") return null;

    const key =
      typeof value === "object" && value !== null
        ? ((value as any).value ?? (value as any).label)
        : value;

    const opts = getOptions() as Option[];

    return (
      opts.find((o) => o.value === key) ??
      opts.find((o) => String(o.value) === String(key)) ??
      opts.find((o) => String(o.label) === String(key)) ??
      null
    );
  };

  return (
    <Box
      sx={{
        minWidth: "200px",
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: customWidth ? customWidth : "auto",
        ...sx,
      }}
    >
      {textTooltip && (
        <TooltipDialogCst inputDialog title={label} textTooltip={textTooltip} />
      )}
      <Autocomplete
        ListboxProps={{
          style: {
            overflow: "auto",
            maxHeight: "220px",
          },
        }}
        size="small"
        disablePortal
        open={open}
        noOptionsText={"No options"}
        getOptionDisabled={(value: any) =>
          getOptionDisabled ? getOptionDisabled(value) : false
        }
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isFilterActive ? theme.palette.text.primary : "",
          },
          "& .MuiAutocomplete-listbox": {
            color: "red",
          },
        }}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        id={id}
        value={getSelectedOption()}
        onInputChange={(event, value, reason) => {
          onChangePagination && onChangePagination(value);
        }}
        getOptionLabel={(opt) =>
          opt?.label !== undefined ? String(opt.label) : ""
        }
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        onChange={(event: any, newValue: Option | null) => {
          onChange(newValue);
        }}
        options={getOptions()}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label={label}
              error={error}
              helperText={error ? errorMessage : ""}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          );
        }}
        PaperComponent={CustomPaper}
        disabled={disabled}
      />
    </Box>
  );
};

export default AutocompleteEventLabelCst;
